import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { TriangleAlert } from "lucide-react";
import { z } from "zod";

import { FormField } from "components/form/FormField/FormField";
import { Button } from "components/ui/Button";
import { Checkbox as CustomCheckbox } from "components/ui/Checkbox";
import { DatePickerField } from "components/ui/DatePickerField";
import { Dialog } from "components/ui/Dialog";
import { DialogFooter } from "components/ui/Dialog/DialogFooter";
import { Select } from "components/ui/Select/Select";
import { getReviewTypeTitle,REVIEW_TYPES } from "features/cdm/constants/reviewTypes";
import { isHighRiskEthnicity } from "features/cdm/ocf/utils/highRiskEthnicity";
import { isPregnancyConditionVisible } from "features/cdm/pp/utils/visibleRegistrationReasons";
import { useEthnicGroups } from "features/patients/hooks/useEthnicGroups";
import { usePatientData } from "features/patients/hooks/usePatientData";
import { usePatientHealthIdentifiers } from "features/patients/hooks/usePatientHealthIdentifiers";
import { resolveEthnicityCode } from "features/patients/utils";
import { useStaffMembers } from "features/practice/hooks/useStaffMembers";
import { onInvalidSubmit } from "utils/reportFormErrors";
import { formatEircode, validateEircode } from "utils/validators";

import style from "./AddOCFReviewDialog.module.css";

const formSchema = z.object({
  consultationDate: z.string().min(1, { message: "Please provide consultation date." }),
  eircode: z
    .string()
    .min(1, { message: "Please provide eircode." })
    .refine(
      (val) => validateEircode(val).isValid,
      (val) => ({ message: validateEircode(val).error || "Invalid Eircode format" })
    ),
  ethnicityCode: z.string().optional(),
  reviewingDoctor: z
    .string({ required_error: "Please provide reviewing doctor." })
    .min(1, { message: "Please provide reviewing doctor." }),
  other: z.boolean().optional(),
  otherText: z.string().optional(),

  currentSmokingStatus: z.boolean(),
  bmi: z.boolean(),
  previousBNP: z.boolean(),
  highRiskEthnicity: z.boolean(),
  historyOfGestationalDiabetes: z.boolean(),
  dyslipidsemia: z.boolean(),
  chronicKidneyDisease: z.boolean(),
  historyOfSevereMentalIllness: z.boolean(),
}).superRefine((data, ctx) => {
  if (data.other && !data.otherText?.trim()) {
    ctx.addIssue({
      code: "custom",
      path: ["otherText"],
      message: "Please provide details for 'Other'.",
    });
  }
});

const OCF_INDICATOR_FIELDS = [
  "currentSmokingStatus",
  "bmi",
  "previousBNP",
  "highRiskEthnicity",
  "historyOfGestationalDiabetes",
  "dyslipidsemia",
  "chronicKidneyDisease",
  "historyOfSevereMentalIllness",
  "other",
];

export const AddOCFReviewDialog = ({
  isAddOcfReviewDialogOpen,
  setIsAddOcfReviewDialogOpen,
  addNewOCFItem,
  doctor,
  reviewType = REVIEW_TYPES.OCF_INITIAL,
}) => {
  const { data: staffMembers } = useStaffMembers();
  const { data: patient } = usePatientData();
  const { data: healthIdentifiers } = usePatientHealthIdentifiers();
  const { data: ethnicGroups } = useEthnicGroups();

  const [showReasonError, setShowReasonError] = useState(false);

  const missingPatientFields = [
    !patient?.ethnicityCode && "Ethnic Group",
    !patient?.address?.country && "Country",
    !patient?.financialClass && "Patient Classification",
  ].filter(Boolean);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      consultationDate: format(new Date(), "yyyy-MM-dd"),
      eircode: "",
      reviewingDoctor: doctor?.staffMember?.id?.toString() ?? "",
      ethnicityCode: "",
      other: false,
      otherText: "",

      currentSmokingStatus: false,
      bmi: false,
      previousBNP: false,
      highRiskEthnicity: false,
      historyOfGestationalDiabetes: false,
      dyslipidsemia: false,
      chronicKidneyDisease: false,
      historyOfSevereMentalIllness: false,
    },
  });

  useEffect(() => {
    form.setValue("reviewingDoctor", doctor?.staffMember?.id?.toString() ?? "");
  }, [doctor, form]);

  useEffect(() => {
    if (isAddOcfReviewDialogOpen && patient?.address?.postCode) {
      form.setValue(
        "eircode",
        formatEircode(patient.address.postCode) || patient.address.postCode
      );
    }
    if (isAddOcfReviewDialogOpen && patient?.ethnicityCode && ethnicGroups?.length) {
      // Normalise the patient's ethnicity (code or description) to the canonical
      // code so the Select — which matches by `g.code` — prefills correctly.
      const code = resolveEthnicityCode(patient.ethnicityCode, ethnicGroups);
      if (code) form.setValue("ethnicityCode", code);
    }
  }, [
    isAddOcfReviewDialogOpen,
    patient?.address?.postCode,
    patient?.ethnicityCode,
    ethnicGroups,
    form,
  ]);

  // The `highRiskEthnicity` indication ("Ethnicity" — PID.22) is DERIVED, never
  // chosen: the spec says "auto populate with Y if PID.22 is one of (Irish
  // Travellers, Roma, Black African, Black Irish, Other Black, Other Asian)"
  // (CDM-spec.md:1593) and allows no clinician override. It always mirrors the
  // form's live ethnicity Select (not the patient record), so re-selecting
  // ethnicity re-derives it; the checkbox below is read-only.
  const watchedEthnicityCode = form.watch("ethnicityCode");

  useEffect(() => {
    if (!ethnicGroups?.length) return;

    const highRisk = isHighRiskEthnicity(watchedEthnicityCode, ethnicGroups);

    if (form.getValues("highRiskEthnicity") !== highRisk) {
      form.setValue("highRiskEthnicity", highRisk);
    }
  }, [watchedEthnicityCode, ethnicGroups, form]);

  const handleEircodeBlur = (e) => {
    const val = e.target.value;
    if (val) {
      form.setValue("eircode", formatEircode(val), { shouldValidate: true });
    }
  };

  const handleFormSubmit = (data) => {
    if (!OCF_INDICATOR_FIELDS.some((field) => data[field])) {
      setShowReasonError(true);

      return;
    }

    const contractDoctor = staffMembers?.find(
      (item) => item.id === Number(data.reviewingDoctor)
    );

    const reviewDate =
      data.consultationDate || format(new Date(), "yyyy-MM-dd");

    addNewOCFItem({
      id: crypto.randomUUID(),
      date: new Date(reviewDate),
      reviewDate,
      name: getReviewTypeTitle(reviewType),
      reviewType,
      status: "Incomplete",
      type: "ocf",
      registeredDate: new Date(),
      reviewingDate: new Date(),
      reviewingDoctor:
        contractDoctor?.firstName + " " + contractDoctor?.lastName,
      createdBy:
        doctor?.staffMember?.firstName + " " + doctor?.staffMember?.lastName,
      ethnicityCode: data.ethnicityCode,

      currentSmokingStatus: data.currentSmokingStatus,
      bmiGreaterThan30: data.bmi,
      previousHighBnp: data.previousBNP,
      highRiskEthnicity: data.highRiskEthnicity,
      historyOfGestationalDiabetes: data.historyOfGestationalDiabetes,
      dyslipidaemia: data.dyslipidsemia,
      chronicKidneyDisease: data.chronicKidneyDisease,
      historyOfSevereMentalIllness: data.historyOfSevereMentalIllness,
      otherText: data.otherText,
    });
    setIsAddOcfReviewDialogOpen(false);
  };

  const importedData = {
    gmsNumber:
      healthIdentifiers?.medicalCardNo || patient?.medicalCardNumber || "",
    contractDoctor:
      doctor?.staffMember?.firstName + " " + doctor?.staffMember?.lastName,
    patientDOB: patient?.dateOfBirth,
  };

  const isAnyIndicatorSelected = OCF_INDICATOR_FIELDS.some((field) =>
    form.watch(field)
  );

  return (
    <Dialog
      open={isAddOcfReviewDialogOpen}
      title="Add Opportunistic Case Finding Review"
      onClose={() => setIsAddOcfReviewDialogOpen(false)}
      width={1100}
    >
      <div style={{ overflowY: "auto", maxHeight: "calc(95vh - 140px)" }}>
        {missingPatientFields.length > 0 && (
          <div style={{ backgroundColor: "#fff3cd", border: "1px solid #ffc107", borderRadius: "12px", padding: "16px", marginBottom: "16px", color: "#856404", fontSize: "14px", fontWeight: "600" }}>
            ⚠️ OCF cannot be submitted — patient is missing {missingPatientFields.join(" and ")}. Please update the patient profile first.
          </div>
        )}
        <div className={style.importedDataWrapper}>
          <div className={style.importedDataItem}>
            <p className={style.importedDataItemLabel}>GMS Number</p>
            <p className={style.importedDataItemValue}>
              {importedData.gmsNumber}
            </p>
          </div>
          <div className={style.importedDataItem}>
            <p className={style.importedDataItemLabel}>Contract Doctor</p>
            <p className={style.importedDataItemValue}>
              {importedData.contractDoctor}
            </p>
          </div>
          <div className={style.importedDataItem}>
            <p className={style.importedDataItemLabel}>Patient DOB</p>
            <p className={style.importedDataItemValue}>
              {importedData.patientDOB}
            </p>
          </div>
        </div>
        <FormProvider {...form}>
          <form
            className={style.addReferralForm}
            onSubmit={form.handleSubmit(handleFormSubmit, onInvalidSubmit("AddOCFReviewDialog"))}
          >
            <div className={style.inputGroupGrid4Columns}>
              <FormField name="consultationDate">
                <label className={style.pregnancyFormLabel}>
                  Consultation date
                </label>
                <DatePickerField
                  value={form.watch("consultationDate")}
                  onChange={(value) =>
                    form.setValue("consultationDate", value, {
                      shouldValidate: true,
                    })
                  }
                  maxDate={new Date()}
                />
              </FormField>
              <FormField name="reviewingDoctor">
                <label className={style.addReferralFormLabel}>
                  Reviewing Doctor
                </label>
                <Select
                  options={(staffMembers ?? []).map((staff) => ({
                    value: staff.id,
                    label: `${staff.firstName} ${
                      staff.lastName
                    }${
                      staff.staffRole
                        ? ` (${staff.staffRole.toLowerCase()})`
                        : ""
                    }${staff.active ? "" : " (inactive)"}`,
                  }))}
                  value={form.watch("reviewingDoctor") ?? ""}
                  onChange={(value) =>
                    form.setValue("reviewingDoctor", value, {
                      shouldValidate: true,
                    })
                  }
                  required
                />
              </FormField>
              <FormField name="ethnicityCode">
                <label className={style.addReferralFormLabel}>Ethnicity</label>
                <Select
                  options={(ethnicGroups ?? []).map((group) => group.description)}
                  value={ethnicGroups?.find((g) => g.code === form.watch("ethnicityCode"))?.description ?? ""}
                  onChange={(value) => {
                    const selectedGroup = ethnicGroups?.find((g) => g.description === value);
                    form.setValue("ethnicityCode", selectedGroup?.code ?? "", { shouldValidate: true });
                  }}
                  required
                />
              </FormField>
              <FormField name="eircode">
                <label className={style.pregnancyFormLabel}>Eircode</label>
                <input
                  type="text"
                  className="form-control"
                  {...form.register("eircode")}
                  onBlur={handleEircodeBlur}
                />
              </FormField>
            </div>
            <div className={style.indicatorsSection}>
              <h2 style={{ fontSize: "18px", fontWeight: "bold" }}>
                Indicators for Review
              </h2>
              {showReasonError && !isAnyIndicatorSelected && (
                <div className={style.reasonRequiredMessage} role="status">
                  <TriangleAlert
                    size={20}
                    className={style.reasonRequiredIcon}
                  />
                  <p className={style.reasonRequiredText}>
                    Select at least one indicator to start the review.
                  </p>
                </div>
              )}
              <fieldset className={style.medsCheckboxGroup}>
                <div className={style.checkboxWrapper}>
                  <label className={style.pregnancyFormCheckboxLabel}>
                    <CustomCheckbox
                      checked={form.watch("currentSmokingStatus")}
                      onChange={(e) => {
                        form.setValue("currentSmokingStatus", e.target.checked);
                      }}
                    />
                    Current Smoking Status
                  </label>
                  <label className={style.pregnancyFormCheckboxLabel}>
                    <CustomCheckbox
                      checked={form.watch("bmi")}
                      onChange={(e) => {
                        form.setValue("bmi", e.target.checked);
                      }}
                    />
                    {"BMI >=30kg/m2"}
                  </label>

                  <label className={style.pregnancyFormCheckboxLabel}>
                    <CustomCheckbox
                      checked={form.watch("previousBNP")}
                      onChange={(e) => {
                        form.setValue("previousBNP", e.target.checked);
                      }}
                    />
                    {"Previous BNP >=34pg/ml or NTproBNP >=125pg/ml"}
                  </label>
                  {/* Read-only: derived from the patient's ethnicity above, per
                      the spec's "auto populate with Y" rule — not a clinician
                      choice. */}
                  <label className={style.pregnancyFormCheckboxLabel}>
                    <CustomCheckbox
                      checked={form.watch("highRiskEthnicity")}
                      disabled
                      readOnly
                      title="Set automatically from the patient's ethnicity"
                    />
                    {"Ethnicity"}
                  </label>
                  {(isPregnancyConditionVisible(patient) ||
                    form.watch("historyOfGestationalDiabetes")) && (
                    <label className={style.pregnancyFormCheckboxLabel}>
                      <CustomCheckbox
                        checked={form.watch("historyOfGestationalDiabetes")}
                        onChange={(e) => {
                          form.setValue(
                            "historyOfGestationalDiabetes",
                            e.target.checked
                          );
                        }}
                      />
                      {"History of Gestational Diabetes"}
                    </label>
                  )}
                  <label className={style.pregnancyFormCheckboxLabel}>
                    <CustomCheckbox
                      checked={form.watch("dyslipidsemia")}
                      onChange={(e) => {
                        form.setValue("dyslipidsemia", e.target.checked);
                      }}
                    />
                    {"Dyslipidaemia (previously recorded)"}
                  </label>
                  <label className={style.pregnancyFormCheckboxLabel}>
                    <CustomCheckbox
                      checked={form.watch("chronicKidneyDisease")}
                      onChange={(e) => {
                        form.setValue("chronicKidneyDisease", e.target.checked);
                      }}
                    />
                    {
                      "Moderate or severe chronic kidney disease (eGFR <60ml/min 1.73m2 previously recorded)"
                    }
                  </label>
                  <label className={style.pregnancyFormCheckboxLabel}>
                    <CustomCheckbox
                      checked={form.watch("historyOfSevereMentalIllness")}
                      onChange={(e) => {
                        form.setValue(
                          "historyOfSevereMentalIllness",
                          e.target.checked
                        );
                      }}
                    />
                    History of severe Mental Illness
                  </label>
                  <label className={style.pregnancyFormCheckboxLabel}>
                    <CustomCheckbox
                      checked={form.watch("other")}
                      onChange={(e) => {
                        form.setValue("other", e.target.checked);
                      }}
                    />
                    Other
                  </label>
                </div>
              </fieldset>
              <FormField name="otherText">
                <textarea
                  className="form-control textareaform-control"
                  disabled={!form.watch("other")}
                  style={{ minHeight: "100px", marginTop: "16px" }}
                  {...form.register("otherText")}
                />
              </FormField>
            </div>
            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setIsAddOcfReviewDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={missingPatientFields.length > 0}>
                Confirm
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </div>
    </Dialog>
  );
};
